import React, { useState, useEffect, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useApiRequest } from "../../../../hooks/useApiRequest";
import { API_BASE_URL, API_ENDPOINTS } from "../../../../constant/constant";
import Popup from "../Dialog";
import VerifiedIcon from "@mui/icons-material/Verified";

const VerifyComponent = ({ attribute }) => {
  const { name, ...rest } = attribute;

  const [open, setOpen] = useState(false);
  const [apiResponse, setApiResponse] = useState({});
  const { isLoading, sendRequest } = useApiRequest();
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [verificationStage, setVerificationStage] = useState("initial"); // 'initial', 'sent', 'verified', 'expired'

  const {
    trigger,
    watch,
    control,
    setError,
    clearErrors,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext();

  const email = watch("email");
  const verifyEmailOTP = watch("verifyEmailOTP");

  useEffect(() => {
    if (verificationStage !== "initial") {
      setVerificationStage("initial");
      setTimeLeft(0);
      clearErrors("verifyEmailOTP");
      resetField("verifyEmailOTP");
    }
  }, [email]);

  useEffect(() => {
    if (verificationStage === "expired") {
      clearErrors("email");
      setError("verifyEmailOTP", {
        type: "manual",
        message: "OTP is expired",
      });
    }
  }, [verificationStage]);

  useEffect(() => {
    if (verificationStage === "verified") {
      setValue("emailVerified", true);
      setTimeLeft(0);
    }
  }, [verificationStage]);

  // Timer management
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      if (verificationStage !== "initial" && verificationStage !== "verified") {
        setVerificationStage("expired");
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  const validateEmail = useCallback(async () => {
    clearErrors("email");
    const isValid = await trigger("email");
    if (!isValid) {
      setError("email", {
        type: "manual",
        message: "Please enter a valid email address",
      });
    }
    return isValid;
  }, [clearErrors, setError, trigger]);

  const validateVerifyEmailOTP = useCallback(async () => {
    clearErrors("verifyEmailOTP");

    // First check OTP field if empty
    if (!verifyEmailOTP) {
      setError("verifyEmailOTP", {
        type: "manual",
        message: "OTP is required",
      });
      return false;
    }

    // Then validate against schema
    const isValid = await trigger("verifyEmailOTP");

    return isValid;
  }, [clearErrors, setError, trigger, verifyEmailOTP]);

  const handleSendOTP = useCallback(async () => {
    clearErrors("verifyEmailOTP");
    const isValid = await validateEmail();
    if (!isValid) return;

    try {
      const response = await sendRequest(
        `${API_BASE_URL}${API_ENDPOINTS.SEND_EMAIL_OTP}`,
        "POST",
        { email }
      );
      if (response?.success) {
        setTimeLeft(90);
        setCanResend(false);
        setVerificationStage("sent");
        setApiResponse({});
      } else {
        setApiResponse(response || { message: "Failed to send OTP" });
        setOpen(true);
      }
    } catch (error) {
      setApiResponse({ message: "An error occurred while sending OTP" });
      setOpen(true);
      console.error("OTP send error:", error);
    }
  }, [email, sendRequest, validateEmail]);

  const handleVerifyOTP = useCallback(async () => {
    const isValid = await validateVerifyEmailOTP();
    if (!isValid) return;

    try {
      const response = await sendRequest(
        `${API_BASE_URL}${API_ENDPOINTS.VERIFY_EMAIL_OTP}`,
        "POST",
        { email, otp: verifyEmailOTP }
      );

      if (response?.success) {
        setVerificationStage("verified");
        setApiResponse({});
      } else {
        setApiResponse(response || { message: "Verification failed" });
        setOpen(true);
      }
    } catch (error) {
      setApiResponse({ message: "An error occurred during verification" });
      setOpen(true);
      console.error("OTP verification error:", error);
    }
  }, [email, sendRequest, setError, clearErrors, validateVerifyEmailOTP]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {errors[name] && <p className="errorMesg">{errors[name].message}</p>}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ height: "100%" }}
          >
            {verificationStage === "verified" ? (
              <Chip
                className="NCT_green_btn"
                icon={<VerifiedIcon color="success" fontSize="medium" />}
                label="Verified"
              />
            ) : (
              <>
                <Chip
                  className="NCT_blue_btn"
                  label={
                    verificationStage === "initial"
                      ? "Send OTP"
                      : canResend
                      ? "Resend OTP"
                      : "Verify OTP"
                  }
                  onClick={
                    verificationStage === "sent"
                      ? handleVerifyOTP
                      : handleSendOTP
                  }
                  disabled={
                    isLoading || (!canResend && verificationStage !== "sent")
                  }
                />
                {verificationStage === "sent" && (
                  <>
                    <input
                      {...field}
                      {...rest}
                      className={`form-control px-3 py-2 ${
                        errors[name] ? "error-red" : ""
                      }`}
                    />
                    <Chip
                      sx={{ background: "#fff", color: "#000" }}
                      label={`${timeLeft}s`}
                    />
                  </>
                )}
              </>
            )}
          </Stack>
        )}
      />
      <Popup open={open} handleClose={handleClose} data={apiResponse} />
    </>
  );
};

export default VerifyComponent;
